import { reportFrontendError } from '../../telemetry'
import { canvasAuthService } from '../services/CanvasAuthService'

export const SALESFORCE_BROKER_EVENT_NAME = 'labra.canvas.referralUpdated'
export const SALESFORCE_BROKER_CHANNEL = 'labra-canvas-referral-updates'
export const SALESFORCE_ACTION_HUB_REFRESH_EVENT =
  'labra:salesforce-action-hub-refresh'
export const SALESFORCE_PENDING_RESOURCE_KEY =
  'labra:salesforce-pending-resource'

type SalesForceBrokerPayload = {
  partnerId: string
  opportunityId?: string | null
  timestamp?: string
}

type ReferralBrokerPayload = SalesForceBrokerPayload & {
  resourceType: 'referral'
  referral: unknown
}

type OfferBrokerPayload = SalesForceBrokerPayload & {
  resourceType: 'offer'
  offer: unknown
}

type ResellerAuthorizationBrokerPayload = SalesForceBrokerPayload & {
  resourceType: 'reseller-auth'
  resellerAuthorization: unknown
  timestamp?: string
}

type SalesforceResourceSavedPayload =
  | ReferralBrokerPayload
  | OfferBrokerPayload
  | ResellerAuthorizationBrokerPayload

export const emitSalesforceResourceSaved = (
  payload: SalesforceResourceSavedPayload
): void => {
  if (typeof window === 'undefined') return

  const hasValidPayload =
    Boolean(payload?.partnerId) &&
    ((payload.resourceType === 'referral' && payload.referral !== undefined) ||
      (payload.resourceType === 'offer' && payload.offer !== undefined) ||
      (payload.resourceType === 'reseller-auth' &&
        payload.resellerAuthorization !== undefined))
  if (!hasValidPayload) return

  const brokerPayload: SalesforceResourceSavedPayload = {
    ...payload,
    timestamp: payload.timestamp || new Date().toISOString(),
  }

  // Resolve targetOrigin: prefer the backend-signed locationUrl stored by
  // CanvasAuthService (set during Canvas JWT exchange), then fall back to
  // document.referrer. If neither yields a parseable origin, skip postMessage
  // entirely rather than using '*' or crashing the call site.
  const rawOriginSource =
    canvasAuthService.getLocationUrl()?.trim() ||
    document.referrer?.trim() ||
    null

  let targetOrigin: string | null = null
  if (rawOriginSource) {
    try {
      targetOrigin = new URL(rawOriginSource).origin
    } catch {
      reportFrontendError(
        'manual',
        new Error(
          `emitSalesforceResourceSaved: origin source "${rawOriginSource}" is not a valid URL; skipping postMessage`
        ),
        { 'salesforce.resource_broker.transport': 'post_message' }
      )
    }
  }

  if (targetOrigin) {
    window.top?.postMessage(
      { name: SALESFORCE_BROKER_EVENT_NAME, payload: brokerPayload },
      targetOrigin
    )
  } else if (!rawOriginSource) {
    reportFrontendError(
      'manual',
      new Error(
        'emitSalesforceResourceSaved: no locationUrl or document.referrer available; skipping postMessage'
      ),
      { 'salesforce.resource_broker.transport': 'post_message' }
    )
  }

  try {
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      const channel = new BroadcastChannel(SALESFORCE_BROKER_CHANNEL)
      channel.postMessage({
        name: SALESFORCE_BROKER_EVENT_NAME,
        payload: brokerPayload,
      })
      channel.close()
    }
  } catch (err) {
    reportFrontendError('manual', err, {
      'salesforce.resource_broker.transport': 'broadcast_channel',
    })
  }

  try {
    window.dispatchEvent(
      new CustomEvent(SALESFORCE_ACTION_HUB_REFRESH_EVENT, {
        detail: brokerPayload,
      })
    )
  } catch (err) {
    reportFrontendError('manual', err, {
      'salesforce.resource_broker.transport': 'action_hub_custom_event',
    })
  }

  try {
    window.sessionStorage.setItem(
      SALESFORCE_PENDING_RESOURCE_KEY,
      JSON.stringify(brokerPayload)
    )
  } catch (err) {
    reportFrontendError('manual', err, {
      'salesforce.resource_broker.transport': 'session_storage',
    })
  }

  // localStorage fires the 'storage' event in OTHER same-origin windows/iframes,
  // giving the action hub a reliable fallback when BroadcastChannel is unavailable
  // (e.g. partitioned contexts in Salesforce Canvas Lightning iframes).
  try {
    window.localStorage.setItem(
      SALESFORCE_PENDING_RESOURCE_KEY,
      JSON.stringify(brokerPayload)
    )
  } catch (err) {
    reportFrontendError('manual', err, {
      'salesforce.resource_broker.transport': 'local_storage',
    })
  }
}
